import { IOfflineSalesDataReport } from '@/Interfaces/Attribution/Report.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { getDefaultOfflineSalesFilter } from '@/helpers/attribution';
import { RootState } from '@/store';
import { fetchOfflineSales } from '@/store/api/attribution.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { OfflineSalesFilters } from '@/types/Attribution/filters.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

enum ActionPrefix {
  FETCH_DATA = 'offlineSales/FETCH_DATA',
}

interface IState {
  filters: OfflineSalesFilters;
  data: IOfflineSalesDataReport;
  dataRequestId: string | null;
}

const initialState: IState = {
  filters: getDefaultOfflineSalesFilter(),
  dataRequestId: null,
  data: {
    extrapolatedConversions: 0,
    impressions: 0,
    matchedConversions: 0,
    matchedSales: 0,
    mediaSalesPercent: 0,
    reach: 0,
    totalSales: 0,
    uniqueCVR: 0,
  },
};

const offlineSalesSlice = createSlice({
  name: 'offlineSales',
  initialState,
  reducers: {
    setFilters: (state, { payload }: { payload: Partial<OfflineSalesFilters> }) => {
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },
    resetOfflineSalesPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getOfflineSalesData.pending, pendingCaseHandler('data'))
      .addCase(getOfflineSalesData.rejected, rejectedCaseHandler('data'))
      .addCase(getOfflineSalesData.fulfilled, fulfilledCaseHandler('data'));
  },
});

export default offlineSalesSlice.reducer;

export const { setFilters, resetOfflineSalesPageState } = offlineSalesSlice.actions;

export const getOfflineSalesData = createAsyncThunk<IOfflineSalesDataReport, undefined, IRejectValueThunkConfig>(
  ActionPrefix.FETCH_DATA,
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        offlineSales: { filters },
      } = getState() as RootState;

      return await fetchOfflineSales(filters);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
);
